import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Sparkles, Loader2, ArrowLeft, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';

const API_URL = `${import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au'}/public/subscription`;

const PLAN_HIGHLIGHTS: Record<string, string[]> = {
  trial: ['3 clients', '50 AI queries', '1 month free trial', 'No credit card'],
  starter: ['10 clients', '500 AI queries/mo', '2 users', '200 documents', 'Xero connection', 'PDF/Word/CSV export'],
  professional: ['50 clients', '2,000 AI queries/mo', '5 users', '10,000 documents', 'ATO compliance suite', 'Firm dashboard'],
  enterprise: ['200 clients', '10,000 AI queries/mo', '10 users', '20,000 documents', 'Tax planning & scenarios', 'Multi-client intelligence'],
  unlimited: ['500 clients', '50,000 AI queries/mo', '20 users', '50,000 documents', 'All features included', 'API access', 'White-label branding'],
};

const PLAN_ORDER = ['trial', 'starter', 'professional', 'enterprise', 'unlimited'];

export default function UpgradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState<string | null>(null);

  // Handle return from Stripe checkout
  useEffect(() => {
    const upgraded = searchParams.get('upgraded');
    const sessionId = searchParams.get('session_id');
    if (upgraded) {
      // Fetch Stripe session to get customer/subscription IDs, then update plan
      const doUpgrade = async () => {
        let stripeCustomerId: string | undefined;
        let stripeSubscriptionId: string | undefined;
        if (sessionId) {
          try {
            const r = await fetch(`${API_URL}/session/${sessionId}`);
            const d = await r.json();
            stripeCustomerId = d.data?.customer;
            stripeSubscriptionId = d.data?.subscription;
          } catch { /* proceed without stripe IDs */ }
        }
        await apiClient.post('/subscription/upgrade', { planName: upgraded, stripeCustomerId, stripeSubscriptionId });
        setUpgradeSuccess(upgraded);
        queryClient.invalidateQueries({ queryKey: ['billing'] });
        toast.success(`Upgraded to ${upgraded.charAt(0).toUpperCase() + upgraded.slice(1)}!`);
      };
      doUpgrade().catch(() => toast.error('Payment received but plan update failed. Contact support.'));
    }
  }, [searchParams, queryClient]);

  // Fetch plans from public API
  const { data: plans, isLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/plans`);
      const d = await r.json();
      return d.data;
    },
  });

  // Get current plan from billing API
  const { data: billing } = useQuery({
    queryKey: ['billing'],
    queryFn: async () => { const r = await apiClient.get('/subscription/billing'); return r.data.data; },
  });

  const currentPlan = upgradeSuccess || billing?.plan?.planName || 'trial';
  const currentPlanIndex = PLAN_ORDER.indexOf(currentPlan);

  const handleUpgrade = async (planName: string) => {
    if (!user?.email) {
      toast.error('Unable to get account email');
      return;
    }
    setIsCheckingOut(true);
    try {
      // First update plan in our DB
      await apiClient.post('/subscription/upgrade', { planName });

      // Then create Stripe checkout for payment
      const r = await fetch(`${API_URL}/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planName,
          email: user.email,
          billingCycle,
          returnUrl: '/panel/upgrade',
        }),
      });
      const d = await r.json();
      if (!d.success) {
        // Plan already updated, but Stripe failed - still show success
        queryClient.invalidateQueries({ queryKey: ['billing'] });
        setUpgradeSuccess(planName);
        toast.success(`Upgraded to ${planName.charAt(0).toUpperCase() + planName.slice(1)}!`);
        setIsCheckingOut(false);
        return;
      }
      if (d.data?.checkoutUrl) {
        window.location.href = d.data.checkoutUrl;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to upgrade');
      setIsCheckingOut(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/billing')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Billing
        </Button>
      </div>

      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Upgrade Your Plan</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Unlock more features, higher limits, and advanced intelligence
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="border rounded-full p-1 flex gap-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={cn(
              'px-5 py-2 rounded-full text-xs tracking-wider uppercase transition-all duration-300',
              billingCycle === 'monthly' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={cn(
              'px-5 py-2 rounded-full text-xs tracking-wider uppercase transition-all duration-300',
              billingCycle === 'yearly' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Yearly <span className="text-[10px] text-green-500 ml-1">Save 20%</span>
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {(plans || []).map((plan: any) => {
          const planIndex = PLAN_ORDER.indexOf(plan.name);
          const isCurrent = plan.name === currentPlan;
          const isDowngrade = planIndex <= currentPlanIndex && !isCurrent;
          const isUpgrade = planIndex > currentPlanIndex;
          const isSelected = selectedPlan === plan.name;
          const planPrice = parseFloat(billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
          const monthlyEq = billingCycle === 'yearly' ? Math.round(planPrice / 12) : planPrice;
          const highlights = PLAN_HIGHLIGHTS[plan.name] || [];
          const isPopular = plan.name === 'enterprise';

          return (
            <div
              key={plan.id}
              className={cn(
                'relative border rounded-lg p-5 transition-all duration-300 flex flex-col',
                isCurrent ? 'border-primary bg-primary/5 ring-1 ring-primary/20' :
                isSelected ? 'border-primary/50 bg-primary/5' :
                isDowngrade ? 'opacity-50' :
                'border-border hover:border-primary/30'
              )}
            >
              {isCurrent && (
                <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] px-2">Current Plan</Badge>
              )}
              {isPopular && !isCurrent && (
                <Badge variant="secondary" className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] px-2">
                  <Zap className="h-2.5 w-2.5 mr-0.5" />Best Value
                </Badge>
              )}

              <h3 className="text-base font-semibold mt-1">{plan.display_name}</h3>

              <div className="my-3">
                {planPrice === 0 ? (
                  <span className="text-2xl font-light">Free</span>
                ) : (
                  <>
                    <span className="text-3xl font-light">${monthlyEq}</span>
                    <span className="text-muted-foreground text-xs">/mo</span>
                    {billingCycle === 'yearly' && <p className="text-[10px] text-muted-foreground mt-0.5">${planPrice}/year</p>}
                  </>
                )}
              </div>

              <ul className="space-y-1.5 mb-5 flex-1">
                {highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs">
                    <Check className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{h}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <Button variant="outline" size="sm" disabled className="w-full text-xs">
                  Current Plan
                </Button>
              ) : isDowngrade ? (
                <Button variant="ghost" size="sm" disabled className="w-full text-xs text-muted-foreground">
                  Downgrade
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="w-full text-xs"
                  variant={isPopular ? 'default' : 'outline'}
                  disabled={isCheckingOut}
                  onClick={() => {
                    setSelectedPlan(plan.name);
                    handleUpgrade(plan.name);
                  }}
                >
                  {isCheckingOut && selectedPlan === plan.name ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Zap className="h-3.5 w-3.5 mr-1" />
                  )}
                  {plan.name === 'trial' ? 'Switch to Free' : 'Upgrade'}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Help */}
      <div className="text-center pb-4">
        <p className="text-xs text-muted-foreground">
          Need a custom plan? <a href="mailto:hello@ronidoc.ai" className="text-primary hover:underline">Contact us</a>
        </p>
      </div>
    </div>
  );
}
